// App.js - Main app with routing, auth guard, PWA setup
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { SyncProvider } from './contexts/SyncContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import { LoadingScreen } from './components/shared';
import FeedbackWidget from './components/FeedbackWidget';
import UpdateBanner from './components/UpdateBanner';
import { isTabletMode } from './utils/tabletMode';

import LoginScreen from './screens/LoginScreen';
import ChangePasswordScreen from './screens/ChangePasswordScreen';
import HomeScreen from './screens/HomeScreen';
import NewOrderScreen from './screens/NewOrderScreen';
import GuidedPhotoScreen from './screens/GuidedPhotoScreen';
import SessionSummaryScreen from './screens/SessionSummaryScreen';
import SavedScreen from './screens/SavedScreen';
import MyOrdersScreen from './screens/MyOrdersScreen';
import OrderSearchScreen from './screens/OrderSearchScreen';
import OrderDetailScreen from './screens/OrderDetailScreen';
import WeeklyReportScreen from './screens/WeeklyReportScreen';
import RDWorkspaceScreen from './screens/RDWorkspaceScreen';
import RDProjectDetailScreen from './screens/RDProjectDetailScreen';
import SettingsScreen from './screens/SettingsScreen';
import ReviewScreen from './screens/ReviewScreen';
import FeedbackScreen from './screens/FeedbackScreen';
import ComplaintsScreen from './screens/ComplaintsScreen';
import ComplaintNewScreen from './screens/ComplaintNewScreen';
import ComplaintDetailScreen from './screens/ComplaintDetailScreen';
import ComplaintReportsScreen from './screens/ComplaintReportsScreen';
import { canEditComplaints, canViewComplaints } from './utils/complaints';

// Route guard
function RequireAuth({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RequireRD({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  // R&D workspace is desktop-only; the tablet app stays in photographer mode.
  if (isTabletMode()) return <Navigate to="/" replace />;
  if (!['R&D', 'R&D Photographer', 'R&D Reviewer', 'Admin'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Complaint module: office roles edit, site-scoped users read their own factory, R&D never.
function RequireComplaints({ children, edit = false }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (!(edit ? canEditComplaints(user) : canViewComplaints(user))) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  // HTTPS enforcement (skipped for local/emulator hosts so a production build
  // can be tested against a dev server: 10.0.2.2 is the Android emulator's Mac).
  useEffect(() => {
    const localHosts = ['localhost', '127.0.0.1', '10.0.2.2'];
    if (process.env.NODE_ENV === 'production' && window.location.protocol !== 'https:' &&
        !localHosts.includes(window.location.hostname)) {
      window.location.href = window.location.href.replace('http:', 'https:');
    }
  }, []);

  return (
    <>
      <SessionTimeoutWarning />
      <UpdateBanner />
      {/* Rendert alleen iets voor de rol Admin. */}
      <FeedbackWidget />
      <Routes>
        {/* Auth */}
        <Route path="/login" element={!user ? <LoginScreen /> : <Navigate to="/" replace />} />
        <Route path="/change-password" element={
          user ? <ChangePasswordScreen forced={!!user?.must_change_password} /> : <Navigate to="/login" replace />
        } />

        {/* Protected routes */}
        <Route path="/" element={<RequireAuth><HomeScreen /></RequireAuth>} />
        <Route path="/new-order" element={<RequireAuth><NewOrderScreen /></RequireAuth>} />
        <Route path="/guided-photo/:orderId/:sessionId" element={<RequireAuth><GuidedPhotoScreen /></RequireAuth>} />
        <Route path="/session-summary/:orderId/:sessionId" element={<RequireAuth><SessionSummaryScreen /></RequireAuth>} />
        <Route path="/saved/:orderId" element={<RequireAuth><SavedScreen /></RequireAuth>} />
        <Route path="/my-orders" element={<RequireAuth><MyOrdersScreen /></RequireAuth>} />
        <Route path="/feedback" element={<RequireAuth><FeedbackScreen /></RequireAuth>} />
        <Route path="/search" element={<RequireAuth><OrderSearchScreen /></RequireAuth>} />
        <Route path="/order/:id" element={<RequireAuth><OrderDetailScreen /></RequireAuth>} />
        <Route path="/report" element={
          <RequireAuth roles={['Reviewer', 'Supervisor', 'Admin', 'R&D']}>
            <WeeklyReportScreen />
          </RequireAuth>
        } />
        <Route path="/review" element={
          <RequireAuth roles={['Reviewer', 'Supervisor', 'Admin', 'R&D', 'R&D Reviewer']}>
            <ReviewScreen />
          </RequireAuth>
        } />
        {/* "Reports" opens the factory report; the open-complaints overview sits one step behind it. */}
        <Route path="/complaints" element={<RequireComplaints><ComplaintReportsScreen /></RequireComplaints>} />
        <Route path="/complaints/list" element={<RequireComplaints><ComplaintsScreen /></RequireComplaints>} />
        <Route path="/complaints/new" element={<RequireComplaints edit><ComplaintNewScreen /></RequireComplaints>} />
        <Route path="/complaints/reports" element={<Navigate to="/complaints" replace />} />
        <Route path="/complaints/:id" element={<RequireComplaints><ComplaintDetailScreen /></RequireComplaints>} />
        <Route path="/settings" element={<RequireAuth><SettingsScreen /></RequireAuth>} />

        {/* R&D */}
        <Route path="/rd" element={<RequireRD><RDWorkspaceScreen /></RequireRD>} />
        <Route path="/rd/:id" element={<RequireRD><RDProjectDetailScreen /></RequireRD>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
        <AuthProvider>
          <ToastProvider>
            <SyncProvider>
              <AppRoutes />
            </SyncProvider>
          </ToastProvider>
        </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
